import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { toast } from "react-toastify";
import {
  getBreakpointFromWidth,
  verticalCompactor,
  Responsive as ResponsiveGridLayout,
  useContainerWidth,
} from "react-grid-layout";
import type { ResponsiveLayouts } from "react-grid-layout";
import {
  getDashboardByCodeApi,
  getRecycleBinDashboardsApi,
} from "src/api/dashboardApi";
import type {
  DashboardPageResponse,
  RecycleBinDashboardsResponse,
} from "src/types/dashboard";
import type { FilterValues } from "src/types/report";
import {
  GRID_BREAKPOINTS,
  GRID_COLS,
  GRID_CONTAINER_PADDING,
  GRID_MARGIN,
  GRID_MARGINS,
  GRID_ROW_HEIGHTS,
} from "src/constants/dashboard";
import WidgetRenderer from "src/components/dashboard/WidgetRenderer";
import DataContextBar from "src/components/dashboard/DataContextBar";
import { buildResponsiveLayouts } from "src/helpers/Dashboard";
import { MOBILE_QUERY, isMobileView } from "src/helpers/responsive";
import useDashboardStore from "src/store/dashboardStore";
import useFilterStore from "src/store/filterStore";
import FilterBar from "src/components/filters";
import {
  applyFilterDateOffset,
  getInitialValues,
  MULTI_SELECT_FILTER_KEYS,
  normalizeMultiSelectFilters,
} from "src/helpers/Filter";
import { startDashboardVisit, track } from "src/telemetry";
import { downloadDashboardPng } from "src/helpers/dashboardExport";

/**
 * Filters whose VALUE may be reported, mirroring FILTER_VALUE_KEYS in the backend's
 * app/api/activity.py. The backend enforces the same list, so anything added here
 * without a matching entry there is dropped server-side — the browser is not
 * trusted to decide what may be stored.
 *
 * A filter NOT listed still contributes its NAME via `filter_keys`; only its value
 * is withheld. That way a future free-text search box cannot start recording what
 * people typed into it just by existing.
 */
const FILTER_VALUE_KEYS = [
  "drug_name",
  "start_date",
  "end_date",
  "group_by",
  // Some dashboards expose `period` where others expose `group_by`; both are
  // dimension pickers and neither can carry a patient identifier.
  "period",
  "date_range",
] as const;

/** Stringify just the allowlisted filters, skipping blanks. */
const reportableFilterValues = (
  fields: FilterValues,
): Record<string, string> => {
  const out: Record<string, string> = {};
  FILTER_VALUE_KEYS.forEach((key) => {
    const value = fields[key];
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      // A multi-select filter (Products) arrives as an array. An empty one
      // means "every product" — the same as unset — so it is skipped like ""
      // above. The backend stores the value as a string and cuts it at 120
      // characters (activity.py _MAX_FILTER_VALUE_LEN), so a long selection is
      // reported only in part.
      if (value.length > 0) out[key] = value.join(", ");
      return;
    }
    out[key] = String(value);
  });
  return out;
};

// Fallback copy for a load failure the API gave no explanation for.
const DASHBOARD_LOAD_FALLBACK = "Failed to load dashboard details.";

const hasDetail = (value: unknown): value is { detail: unknown } =>
  typeof value === "object" && value !== null && "detail" in value;

/**
 * The backend's own explanation for a failed dashboard load, or null when it
 * didn't send one.
 *
 * This is what makes a restricted dashboard reached by URL readable: the
 * endpoint answers 403 with `{detail: "..."}` (it documents 403 as "User type
 * not authorized for this dashboard"), and showing that beats the generic
 * "Failed to load dashboard details." the page used to print for every error
 * alike.
 *
 * Gated on `detail` actually being present, and reuses `error.message`
 * because api_instance's response interceptor has already flattened `detail`
 * onto it — including the array form FastAPI returns on 422. Without that
 * gate this would surface axios's own generated text ("Network Error",
 * "Request failed with status code 500"), which means nothing to a user.
 */
const apiDetailMessage = (error: unknown): string | null => {
  if (!isAxiosError(error)) return null;
  if (!hasDetail(error.response?.data)) return null;
  return error.message.trim() || null;
};

export default function DashboardPage() {
  const [appliedFilters, setAppliedFilters] = useState<FilterValues>({});
  // The filters panel is 230px wide. On a phone or a portrait tablet that is
  // most of the screen, and it leaves the grid too narrow to read whatever the
  // layout does — so below this width it starts as the off-canvas drawer the
  // `filter-collapsed` styles already implement, reachable from its handle.
  const [filterCollapsed, setFilterCollapsed] = useState(isMobileView);

  // Follow the media query as the window is resized or the device rotated,
  // but only on an actual crossing: a manual toggle inside a breakpoint is
  // left alone rather than being overridden on the next render.
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (event: MediaQueryListEvent) =>
      setFilterCollapsed(event.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  const [filterDisplayValues, setFilterDisplayValues] = useState<FilterValues>(
    {},
  );

  const { dashboard_name } = useParams<{ dashboard_name: string }>();
  const { width, containerRef, mounted } = useContainerWidth({
    initialWidth: 900,
  });
  const { defaultDashboard } = useDashboardStore();
  const { savedFilters, updateSavedFilters, resetSavedFilters } =
    useFilterStore();
  const navigate = useNavigate();

  const {
    data: dashboardResponse,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
    error: dashboardError,
  } = useQuery<DashboardPageResponse, Error>({
    queryKey: ["dashboard_name", dashboard_name],
    // Forwarding the signal lets react-query actually abort this request (and,
    // by extension, the widget queries below) when the user switches dashboards
    // before it resolves — without it, react-query's automatic cancel-on-
    // key-change is a no-op and the abandoned request just runs to completion.
    queryFn: async ({ signal }) =>
      getDashboardByCodeApi(dashboard_name as string, { signal }),
    enabled: !!dashboard_name,
    // Overrides the app-wide 60-minute staleTime (App.tsx). This payload is
    // the dashboard's own definition — its widget list, filters and layout —
    // and it changes from outside this tab: the builder saves a layout, or a
    // widget is deactivated in the portal, which deletes its placement on
    // every dashboard server-side. Cached, a user keeps seeing widgets that no
    // longer exist, so this is always refetched on mount and on tab refocus.
    // The cached copy still renders while that request is in flight, so
    // revisiting a dashboard doesn't flash a loading state.
    staleTime: 0,
  });

  const { data: recycleBinDashboards } = useQuery<
    RecycleBinDashboardsResponse,
    Error
  >({
    queryKey: ["dashboards", "recycle-bin"],
    queryFn: () => getRecycleBinDashboardsApi(),
    staleTime: 0,
  });

  // Filters chosen by the user persist across dashboards via the filter store.
  // Each dashboard only picks up the saved keys it declares itself, so stale
  // values from other dashboards never leak into its widget queries.
  const setInitialFilters = (useSaved = true) => {
    if (dashboardResponse?.filters) {
      const initialFilterValues = getInitialValues(dashboardResponse.filters);
      const carriedOver = useSaved
        ? Object.fromEntries(
            Object.entries(savedFilters).filter(
              ([key]) => key in initialFilterValues,
            ),
          )
        : {};
      // Multi-select keys are forced to the array form here as well as in the
      // filter bar, because these are the values the first widget request goes
      // out with — before the user has touched a control.
      const values = normalizeMultiSelectFilters(
        { ...initialFilterValues, ...carriedOver },
        MULTI_SELECT_FILTER_KEYS,
      );
      setFilterDisplayValues(values);
      setAppliedFilters(applyFilterDateOffset(values));
    }
  };

  useEffect(() => {
    setInitialFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardResponse]);

  // Time on dashboard. Keyed on dashboard_name so navigating between dashboards
  // closes one visit and opens the next; the cleanup emits `dashboard.dwell`
  // carrying BOTH wall-clock and idle-gated time, and drains any pending KPI
  // hovers for this visit.
  useEffect(() => {
    if (!dashboard_name) return;
    const visit = startDashboardVisit(dashboard_name);
    return () => visit.end();
  }, [dashboard_name]);

  const visibleWidgets = useMemo(
    () =>
      (dashboardResponse?.widgets ?? [])
        .filter((widget) => widget.is_visible)
        .sort((a, b) => a.position - b.position),
    [dashboardResponse?.widgets],
  );

  // One layout per breakpoint. Previously this cloned the same 24-column
  // coordinates into all five, which left narrow screens with every widget
  // crammed into one column — see buildResponsiveLayouts.
  const layouts = useMemo<ResponsiveLayouts>(
    () => buildResponsiveLayouts(visibleWidgets) as ResponsiveLayouts,
    [visibleWidgets],
  );

  // RGL takes a single margin/rowHeight rather than a map, so the active
  // breakpoint is resolved here from the same container width it uses.
  const activeBreakpoint = useMemo(
    () => getBreakpointFromWidth(GRID_BREAKPOINTS, width),
    [width],
  );
  const gridMargin = GRID_MARGINS[activeBreakpoint] ?? GRID_MARGIN;
  const gridRowHeight = GRID_ROW_HEIGHTS[activeBreakpoint] ?? 72;

  useEffect(() => {
    if (!dashboard_name && defaultDashboard)
      navigate(`/dashboards/${defaultDashboard}`);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard_name, defaultDashboard]);

  // Screenshots the whole .dashboard-content scroll container, so the PNG
  // includes the widgets below the fold rather than just what's on screen.
  const [isCapturing, setIsCapturing] = useState(false);
  // Cloned into the top of the capture so the PNG carries the dashboard name
  // and the data-period line, which live outside the scroll container.
  const titleRef = useRef<HTMLDivElement>(null);
  const dataContextRef = useRef<HTMLParagraphElement>(null);
  const handleDownloadView = async () => {
    const container = containerRef.current;
    if (!container || isCapturing) return;
    setIsCapturing(true);
    try {
      await downloadDashboardPng(container, {
        dashboardName: dashboardResponse?.dashboard?.name ?? dashboard_name,
        headerNodes: [titleRef.current, dataContextRef.current],
      });
    } catch {
      toast.error("Could not capture the dashboard view.");
    } finally {
      setIsCapturing(false);
    }
  };

  const handleFilterCollapseToggle = () => {
    setFilterCollapsed((prev) => !prev);
  };

  // Applying filters hides the panel so the results are visible straight away
  // -- but only on mobile, where the panel is an overlay drawer sitting on top
  // of the dashboard. On desktop it sits beside the content and closing it on
  // every Apply would just make the user reopen it to change one more filter.
  const handleFiltersApplied = () => {
    if (isMobileView()) {
      setFilterCollapsed(true);
    }
  };

  return (
    <div className="container-fluid px-4 px-lg-5 py-3 d-flex flex-column flex-fill dashboard-scroll">
      <div className="d-flex align-items-center" ref={titleRef}>
        <h3 className="me-2 mb-0 fw-bold text-primary">
          {dashboardResponse?.dashboard?.name ?? "Dashboard"}
        </h3>
      </div>

      {!dashboard_name && (
        <p className="text-muted mb-0">Select a dashboard from the header.</p>
      )}

      {!!dashboard_name && isDashboardLoading && (
        <div className="loader-icon d-flex align-items-center justify-content-center">
          <div className="spinner-border"></div>
        </div>
      )}

      {!!dashboard_name && isDashboardError && (
        <div className="d-flex align-items-center justify-content-center flex-column flex-fill">
          <p className="fs-5 mb-0 text-center">
            {apiDetailMessage(dashboardError) ?? DASHBOARD_LOAD_FALLBACK}
          </p>
        </div>
      )}

      {!!dashboard_name &&
        !isDashboardLoading &&
        !isDashboardError &&
        visibleWidgets.length === 0 && (
          <div className="d-flex align-items-center justify-content-center flex-column flex-fill">
            <p className="fs-5 mb-0">
              No visible widgets configured for this dashboard.
            </p>
          </div>
        )}
      {!!dashboard_name &&
        !isDashboardLoading &&
        !isDashboardError &&
        visibleWidgets.length > 0 && (
          <DataContextBar
            ref={dataContextRef}
            appliedFilters={appliedFilters}
            // Same option list the Products dropdown is built from, so the bar
            // can mask a demo account's product names the way the dropdown does.
            productOptions={
              dashboardResponse?.filters?.find(
                (filter) => filter.key === "drug_name",
              )?.static_options
            }
            onDownloadView={handleDownloadView}
            isDownloadingView={isCapturing}
          />
        )}
      <div className="d-flex mt-2 flex-fill gap-4">
        {!!dashboard_name &&
        !isDashboardLoading &&
        !isDashboardError &&
        dashboardResponse?.filters &&
        dashboardResponse?.filters.length > 0 &&
        visibleWidgets.length > 0 ? (
          <aside
            className={`left-panel ${filterCollapsed ? "filter-collapsed" : ""}`}
          >
            <div className="card filters-panel p-3 shadow">
              <button
                type="button"
                className="btn collapse-btn"
                onClick={handleFilterCollapseToggle}
              >
                <i className="bi bi-chevron-compact-left"></i>
              </button>
              <FilterBar
                inputFields={dashboardResponse.filters}
                defaultValues={filterDisplayValues}
                multiSelectKeys={MULTI_SELECT_FILTER_KEYS}
                isLoading={isDashboardLoading}
                autoSubmitOnTabChange
                onFiltersApplied={handleFiltersApplied}
                onClear={() => {
                  resetSavedFilters();
                  setInitialFilters(false);
                  track("filter.cleared", { dashboardCode: dashboard_name });
                }}
                onSubmit={({ fields }) => {
                  setFilterDisplayValues((prev) => ({ ...prev, ...fields }));
                  setAppliedFilters((prev) => ({
                    ...prev,
                    ...applyFilterDateOffset(fields),
                  }));
                  updateSavedFilters(fields);
                  track("filter.applied", {
                    dashboardCode: dashboard_name,
                    properties: {
                      // Names of everything they changed, values of the safe ones.
                      filter_keys: Object.keys(fields),
                      filter_values: reportableFilterValues(fields),
                    },
                  });
                }}
              />
              <div className="recycle-btn">
                <button
                  type="button"
                  className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2"
                  onClick={() => navigate("/recycle-bin")}
                >
                  <i className="bi bi-recycle" />
                  <span>Recycle Bin</span>
                  <span className="badge text-bg-danger">
                    {recycleBinDashboards?.total ?? 0}
                  </span>
                </button>
              </div>
            </div>
          </aside>
        ) : null}
        <div className="dashboard-content flex-fill" ref={containerRef}>
          {!!dashboard_name &&
            !isDashboardLoading &&
            !isDashboardError &&
            visibleWidgets.length > 0 &&
            mounted && (
              <ResponsiveGridLayout
                width={width}
                layouts={layouts}
                breakpoints={GRID_BREAKPOINTS}
                cols={GRID_COLS}
                rowHeight={gridRowHeight}
                margin={gridMargin}
                containerPadding={GRID_CONTAINER_PADDING}
                compactor={verticalCompactor}
                dragConfig={{ enabled: false }}
                resizeConfig={{ enabled: false }}
              >
                {visibleWidgets.map((widget) => {
                  return (
                    <WidgetRenderer
                      // key={`${dashboard_name}-${widget.id}`}
                      key={String(widget.id)}
                      className="flex-fill"
                      widget={widget}
                      dashboard_name={dashboard_name as string}
                      appliedFilters={appliedFilters}
                    />
                  );
                })}
              </ResponsiveGridLayout>
            )}
        </div>
      </div>
    </div>
  );
}
